import React from 'react';
import { Navigate, useRouteError } from 'react-router-dom';

import RootLayout from 'Component/Layout';

import Splash from '@/screens/Splash';
import { LoginPin, CreatePin, ConfirmPin, RenewPin } from '@screens/Pin';
import Otp from '@screens/Otp';
import Maintenance from '@screens/Maintenance';

import Kyc from '@screens/Kyc';
import KycResubmit from '@screens/KycResubmit';
import { KycRenewalData } from '@screens/Kyc/KycRenewalData';

import { DayTradingDashboard } from '@screens/DayTrading';
import Glossary from '@screens/Glossary';
import Simulator from '@screens/Simulator';
import { Referral, MagicCarpet, CheckRewardReferral } from '@screens/Referral';
import Rdn from '@screens/Rdn';
import Profile from '@screens/Profile';
import { FAQ, FAQDetail } from '@screens/FAQ';
import {
  MarginTrading,
  MTActivation,
  MTActivationStatus,
  MTOnboarding,
} from '@screens/MarginTrading';
import { PnL } from '@/screens/PnL';
import { SecurityCheckup, TwoFA, DeviceSuspended, TrustedDevice } from '@screens/TrustedDevice';
import Layout from '../../Layout';
import FirstRoute from '../FirstRoute';
import PrivateRoute from '../PrivateRoute';

import homeRoutes from './home';

function ReactRouterError() {
  // React-router will catch error before custom ErrorBoundary
  // Re-throw error so that the error will be caught by ErrorBoundary, and allow errors to be logged
  // https://github.com/remix-run/react-router/discussions/10166
  const error = useRouteError();
  throw error;
}

/** @type {import('react-router-dom').RouteObject[]} */
const routes = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ReactRouterError />,
    children: [
      {
        index: true,
        element: <Navigate to="/loading" replace />,
      },
      {
        path: 'loading',
        element: <FirstRoute component={Splash} />,
      },

      // ========== BEFORE PIN ========== //
      {
        path: 'pin',
        element: <FirstRoute component={LoginPin} />,
      },
      {
        path: 'create-pin',
        element: <FirstRoute component={CreatePin} />,
      },
      {
        path: 'renew-pin',
        element: <FirstRoute component={RenewPin} />,
      },
      {
        path: 'validate-pin',
        element: <FirstRoute component={ConfirmPin} />,
      },
      {
        path: '2fa',
        element: <FirstRoute component={TwoFA} />,
      },
      {
        path: 'suspended',
        element: <FirstRoute component={DeviceSuspended} />,
      },

      // ========== AFTER PIN ========== //
      {
        path: 'otp',
        element: <PrivateRoute component={Otp} />,
      },
      {
        path: 'security-check',
        element: <PrivateRoute component={SecurityCheckup} />,
      },
      {
        path: 'maintenance',
        element: <PrivateRoute component={Maintenance} />,
      },

      {
        path: 'kyc',
        element: <PrivateRoute component={Kyc} />,
      },
      {
        path: 'kyc/:activeScreen',
        element: <PrivateRoute component={Kyc} />,
      },
      {
        path: 'kyc-resubmit',
        element: <PrivateRoute component={KycResubmit} />,
      },
      {
        path: 'kyc-renewal-data',
        element: <PrivateRoute component={KycRenewalData} />,
      },
      {
        path: 'referral',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <Referral />,
          },
        ],
      },
      {
        path: 'referral/magic-carpet/:type',
        element: <PrivateRoute component={MagicCarpet} />,
      },
      {
        path: 'referral/check-reward',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <CheckRewardReferral />,
          },
        ],
      },
      {
        path: 'trusted-device',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <TrustedDevice />,
          },
        ],
      },
      {
        path: 'account-detail',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <Profile />,
          },
        ],
      },
      {
        path: 'rdn',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <Rdn />,
          },
        ],
      },
      {
        path: 'faq',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            path: '*',
            element: <FAQ />,
          },
        ],
      },
      {
        path: 'faq-detail',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <FAQDetail />,
          },
        ],
      },
      {
        path: 'simulator',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <Simulator />,
          },
        ],
      },
      {
        path: 'glossary',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <Glossary />,
          },
        ],
      },
      {
        path: 'margin-trading',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <MarginTrading />,
          },
        ],
      },
      {
        path: 'margin-trading-activation',
        element: <PrivateRoute component={MTActivation} />,
      },
      {
        path: 'margin-trading-status',
        element: <PrivateRoute component={MTActivationStatus} />,
      },
      {
        path: 'margin-trading-onboarding',
        element: <PrivateRoute component={Layout} profileOnly withBottomPanel={false} />,
        children: [
          {
            index: true,
            element: <MTOnboarding />,
          },
        ],
      },
      {
        path: 'day-trading',
        element: <PrivateRoute component={DayTradingDashboard} />,
      },
      {
        path: 'pnl',
        element: <PrivateRoute component={Layout} />,
        children: [
          {
            index: true,
            element: <PnL />,
          },
        ],
      },
      {
        path: 'home',
        element: <PrivateRoute component={Layout} />,
        children: homeRoutes,
      },
      {
        path: '*',
        element: <Navigate to="/home" />,
      },
    ],
  },
];

export default routes;
